import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { catalogTone } from '../utils/catalogColors';
import {
  RefreshCw, Check, ChevronDown, ChevronUp, ChevronRight,
  AlertCircle, CheckCircle, Loader2, Bell, ArrowRight, Printer
} from 'lucide-react';

interface SyncRequest {
  id: number;
  organization_id: number;
  store_id: number;
  change_type: string;
  change_summary: string;
  change_data: any;
  status: string;
  created_by_name: string;
  organization_name: string;
  created_at: string;
}

const OrgSyncBanner: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { store } = useAuth();
  const [requests, setRequests] = useState<SyncRequest[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [expandedRequestId, setExpandedRequestId] = useState<number | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const getFieldLabels = useCallback((): Record<string, string> => ({
    name: t('admin:fieldLabels.name'),
    base_price: t('admin:fieldLabels.base_price'),
    vat: t('admin:fieldLabels.vat'),
    category_name: t('admin:fieldLabels.category_name'),
    is_available: t('admin:fieldLabels.is_available'),
    description: t('admin:fieldLabels.description'),
    display_order: t('admin:fieldLabels.display_order'),
    is_active: t('admin:fieldLabels.is_active'),
    price_take_away: t('admin:fieldLabels.price_take_away'),
    price_delivery: t('admin:fieldLabels.price_delivery'),
    price_dine_in: t('admin:fieldLabels.price_dine_in'),
    color: t('products:catalogColor.label'),
  }), [t]);

  const formatDiffValue = useCallback((key: string, val: any): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? t('common:yes') : t('common:no');
    // Χρώμα (migration 264): το ελληνικό όνομα του τόνου, όχι 'amber'.
    if (key === 'color') return catalogTone(val) ? t(`products:catalogColor.tones.${val}`) : '—';
    if (['base_price', 'price_take_away', 'price_delivery', 'price_dine_in'].includes(key)) return `${Number(val).toFixed(2)}€`;
    if (key === 'vat') return `${val}%`;
    return String(val);
  }, [t]);

  const loadPending = useCallback(async () => {
    if (!store?.id) return;
    try {
      const data = await organizationAPI.getPendingSyncRequests(store.id);
      setRequests(data.requests || []);
    } catch {
      // Silently fail — store may not be in an org
    }
  }, [store?.id]);

  useEffect(() => {
    loadPending();
    // Poll every 60s
    const interval = setInterval(loadPending, 60000);
    return () => clearInterval(interval);
  }, [loadPending]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async (id: number) => {
    try {
      setProcessing(id);
      await organizationAPI.approveSyncRequest(id);
      setRequests(prev => prev.filter(r => r.id !== id));
      showToast('success', t('admin:changeApproved'));
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedApprove'));
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveAll = async () => {
    if (!store?.id) return;
    if (!window.confirm(t('admin:approveAllConfirm', { count: requests.length }))) return;
    try {
      setProcessing(-1);
      await organizationAPI.approveAllSyncRequests(store.id);
      setRequests([]);
      showToast('success', t('admin:allChangesApproved'));
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
      await loadPending();
    } finally {
      setProcessing(null);
    }
  };

  const handlePrint = () => {
    const fieldLabels = getFieldLabels();
    const changeTypeLabels: Record<string, string> = {
      category_added: t('admin:changeTypes.category_added'),
      category_updated: t('admin:changeTypes.category_updated'),
      category_removed: t('admin:changeTypes.category_removed'),
      product_added: t('admin:changeTypes.product_added'),
      product_updated: t('admin:changeTypes.product_updated'),
      product_removed: t('admin:changeTypes.product_removed'),
      size_added: t('admin:changeTypes.size_added'),
      size_updated: t('admin:changeTypes.size_updated'),
      size_removed: t('admin:changeTypes.size_removed'),
      ingredient_added: t('admin:changeTypes.ingredient_added'),
      ingredient_updated: t('admin:changeTypes.ingredient_updated'),
      ingredient_removed: t('admin:changeTypes.ingredient_removed'),
      bulk_sync: t('admin:changeTypes.bulk_sync'),
      price_bulk: t('admin:changeTypes.price_bulk'),
    };

    let rows = '';
    for (const req of requests) {
      const data = typeof req.change_data === 'string' ? JSON.parse(req.change_data) : (req.change_data || {});
      const before = data._before || null;
      const typeLabel = changeTypeLabels[req.change_type] || req.change_type;

      let detailsHtml = '';
      // Show new values for adds
      if (req.change_type.endsWith('_added') || req.change_type === 'bulk_sync') {
        const keys = Object.keys(data).filter(k => !k.startsWith('_') && k !== 'org_product_id' && k !== 'org_category_id' && k !== 'org_size_id');
        if (keys.length > 0) {
          detailsHtml = '<table style="margin-top:4px;font-size:11px;border-collapse:collapse;">' +
            keys.map(k => {
              let v = data[k];
              if (v === null || v === undefined) return '';
              if (typeof v === 'boolean') v = v ? t('common:yes') : t('common:no');
              if (typeof v === 'number' && ['base_price','price_take_away','price_delivery','price_dine_in'].includes(k)) v = Number(v).toFixed(2) + '€';
              if (k === 'vat') v = v + '%';
              if (k === 'color') v = formatDiffValue(k, v);
              const label = fieldLabels[k] || k;
              return '<tr><td style="padding:1px 8px 1px 0;color:#666;">' + label + '</td><td style="padding:1px 0;font-weight:500;">' + v + '</td></tr>';
            }).join('') + '</table>';
        }
      }
      // Show diffs for updates
      if (before && Object.keys(before).length > 0) {
        detailsHtml = '<table style="margin-top:4px;font-size:11px;border-collapse:collapse;">' +
          Object.keys(before).map(k => {
            const oldV = formatDiffValue(k, before[k]);
            const newV = formatDiffValue(k, data[k]);
            if (oldV === newV) return '';
            const label = fieldLabels[k] || k;
            return '<tr><td style="padding:1px 8px 1px 0;color:#666;">' + label + '</td>' +
              '<td style="padding:1px 4px;text-decoration:line-through;color:#c00;">' + oldV + '</td>' +
              '<td style="padding:1px 4px;">→</td>' +
              '<td style="padding:1px 0;font-weight:600;color:#060;">' + newV + '</td></tr>';
          }).join('') + '</table>';
      }

      rows += '<tr style="border-bottom:1px solid #ddd;">' +
        '<td style="padding:8px;vertical-align:top;"><span style="display:inline-block;padding:2px 8px;border-radius:4px;background:#f0f0f0;font-size:11px;">' + typeLabel + '</span></td>' +
        '<td style="padding:8px;vertical-align:top;">' + req.change_summary + detailsHtml + '</td>' +
        '<td style="padding:8px;vertical-align:top;font-size:11px;color:#666;">' + new Date(req.created_at).toLocaleString('el-GR') + '</td>' +
        '</tr>';
    }

    const orgName = requests[0]?.organization_name || t('admin:orgGroupLabel');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t('admin:printCatalogChangesTitle')}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;color:#333;} table{width:100%;border-collapse:collapse;}
      th{background:#f8f8f8;text-align:left;padding:8px;border-bottom:2px solid #ccc;font-size:12px;} h1{font-size:18px;margin-bottom:4px;} h2{font-size:13px;color:#666;margin-top:0;}
      @media print{body{padding:0;}}</style></head><body>
      <h1>${t('admin:printCatalogChangesTitle')} — ${orgName}</h1>
      <h2>${t('admin:printSummaryLine', { count: requests.length, date: new Date().toLocaleString('el-GR') })}</h2>
      <table><thead><tr><th style="width:140px;">${t('admin:printTypeHeader')}</th><th>${t('admin:printDetailsHeader')}</th><th style="width:130px;">${t('admin:printDateHeader')}</th></tr></thead>
      <tbody>${rows}</tbody></table></body></html>`;

    const printWin = window.open('', '_blank', 'width=800,height=600');
    if (printWin) {
      printWin.document.write(html);
      printWin.document.close();
      printWin.focus();
      setTimeout(() => printWin.print(), 500);
    }
  };

  // Don't render if no pending requests
  if (requests.length === 0) return null;

  const fmtDate = (d: string) => new Date(d).toLocaleString('el-GR', { dateStyle: 'short', timeStyle: 'short' });
  const fieldLabels = getFieldLabels();

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
          {toast.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {toast.message}
        </div>
      )}

      {/* Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl overflow-hidden mb-4">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-500/5 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-amber-700 dark:text-amber-400" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {t('admin:pendingChangesCount', { count: requests.length, suffix: requests.length === 1 ? 'ή' : 'ές' })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-700/60 dark:text-amber-400/60">{requests[0]?.organization_name}</span>
            {expanded ? <ChevronUp className="h-4 w-4 text-amber-700 dark:text-amber-400" /> : <ChevronDown className="h-4 w-4 text-amber-700 dark:text-amber-400" />}
          </div>
        </button>

        {/* Expanded list */}
        {expanded && (
          <div className="border-t border-amber-500/20">
            {/* Action buttons */}
            <div className="px-4 py-2 flex justify-end gap-2 border-b border-amber-500/10">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-3 hover:bg-surface-4 text-t1 rounded-lg text-xs font-medium transition-colors"
              >
                <Printer className="h-3 w-3" /> {t('common:print')}
              </button>
              <button
                onClick={handleApproveAll}
                disabled={processing !== null}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
              >
                {processing === -1 ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                {t('admin:approveAll')}
              </button>
            </div>

            {/* Request list */}
            <div className="divide-y divide-amber-500/10 max-h-80 overflow-y-auto">
              {requests.map(req => {
                const data = typeof req.change_data === 'string' ? JSON.parse(req.change_data) : (req.change_data || {});
                const before = data._before || null;
                const hasDiff = before && Object.keys(before).length > 0;
                const sizesArr = Array.isArray(data.sizes) ? data.sizes : [];
                const groupsArr = Array.isArray(data.option_groups) ? data.option_groups : [];
                const isProductLike = req.change_type === 'product_updated' || req.change_type === 'product_added';
                // When there's no stored diff (no _before), let the store expand to see the
                // incoming product snapshot (sizes with their prices / option groups) it is approving.
                const hasSnapshot = isProductLike && (sizesArr.length > 0 || groupsArr.length > 0);
                const canExpand = hasDiff || hasSnapshot;
                const isExpanded = expandedRequestId === req.id;

                return (
                  <div key={req.id} className="hover:bg-amber-500/5 transition-colors">
                    <div className="px-4 py-3 flex items-center justify-between">
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        {canExpand && (
                          <button onClick={() => setExpandedRequestId(isExpanded ? null : req.id)} className="text-t4 hover:text-amber-700 dark:hover:text-amber-400 flex-shrink-0">
                            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm text-t1 truncate">{req.change_summary}</p>
                          <p className="text-xs text-t3 mt-0.5">
                            {req.created_by_name || t('admin:systemUser2')} · {fmtDate(req.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                        {/* Approve only — org changes are mandatory (no reject). Anything still
                            pending is auto-applied at register close. */}
                        <button
                          onClick={() => handleApprove(req.id)}
                          disabled={processing !== null}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white disabled:opacity-50 transition-all text-xs font-medium"
                          title={t('admin:approveTitle')}
                        >
                          {processing === req.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          <span>{t('admin:approveTitle', { defaultValue: 'Αποδοχή' })}</span>
                        </button>
                      </div>
                    </div>
                    {/* Expanded detail: real before→after diff when stored, else the
                        incoming product snapshot the store is approving. */}
                    {isExpanded && canExpand && (
                      <div className="px-4 pb-3 pl-10">
                        <div className="bg-surface-0/50 rounded-lg p-3">
                          {hasDiff ? (
                            <div className="space-y-1.5">
                              {Object.keys(before).map(key => {
                                const label = fieldLabels[key] || key;
                                const oldVal = formatDiffValue(key, before[key]);
                                const newVal = formatDiffValue(key, data[key]);
                                if (oldVal === newVal) return null;
                                return (
                                  <div key={key} className="flex items-center gap-2 text-xs">
                                    <span className="text-t4 w-24 flex-shrink-0">{label}</span>
                                    <span className="text-red-600 dark:text-red-400 line-through">{oldVal}</span>
                                    <ArrowRight className="h-3 w-3 text-t4 flex-shrink-0" />
                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">{newVal}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="space-y-2 text-xs">
                              {/* Η τιμή ζει μόνο στα μεγέθη (Φ5): το στιγμιότυπο δεν δείχνει τιμή προϊόντος. */}
                              {data.vat != null && (
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-t2">
                                  <span>{fieldLabels['vat'] || 'ΦΠΑ'}: {Number(data.vat)}%</span>
                                </div>
                              )}
                              {sizesArr.length > 0 && (
                                <div>
                                  <p className="text-t4 uppercase tracking-wide text-[10px] mb-1">{t('admin:sizesLabel', { defaultValue: 'Μεγέθη' })}</p>
                                  <div className="flex flex-wrap gap-1">
                                    {sizesArr.map((s: any, i: number) => {
                                      // Η τιμή του μεγέθους είναι ΑΠΟΛΥΤΗ (Φ5) — όχι προσαύξηση πάνω σε τιμή προϊόντος.
                                      const eff = Number(s.price_modifier) || 0;
                                      return (
                                        <span key={i} className="bg-surface-2 px-1.5 py-0.5 rounded text-t2">
                                          {s.name}{eff > 0 ? <span className="text-emerald-700 dark:text-emerald-300"> {eff.toFixed(2)}€</span> : ''}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {groupsArr.length > 0 && (
                                <div>
                                  <p className="text-t4 uppercase tracking-wide text-[10px] mb-1">{t('admin:optionGroupsLabel', { defaultValue: 'Ομάδες επιλογών' })}</p>
                                  <div className="space-y-1">
                                    {groupsArr.map((g: any, i: number) => (
                                      <div key={i} className="text-t2">
                                        <b className="text-t1">{g.name}</b>
                                        {Array.isArray(g.choices) && g.choices.length > 0 && (
                                          <span className="text-t3"> — {g.choices.map((c: any) => {
                                            const m = Number(c.price_modifier) || 0;
                                            return m ? `${c.name} (${m > 0 ? '+' : ''}${m.toFixed(2)}€)` : c.name;
                                          }).join(', ')}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OrgSyncBanner;
